import { useMemo } from 'react';
import type { WebFrequency, FrequencyHistoryEvent } from '@/mocks/superAdminFrequencies';
import { getFrequencyHistory } from '@/mocks/superAdminFrequencies';

interface FrequencyHistoryModalProps {
  frequency: WebFrequency | null;
  onClose: () => void;
}

function actionLabel(action: FrequencyHistoryEvent['action']): string {
  switch (action) {
    case 'asignacion': return 'Asignación';
    case 'liberacion': return 'Liberación';
    case 'reserva': return 'Reserva';
    case 'cancelacion_reserva': return 'Cancelación reserva';
    case 'solicitud_preferida': return 'Sol. Preferida';
    case 'activacion_preferida': return 'Activ. Preferida';
    case 'transferencia': return 'Transferencia';
    case 'bloquear': return 'Bloqueo';
    case 'reactivar': return 'Reactivación';
    default: return action;
  }
}

function actionColor(action: FrequencyHistoryEvent['action']): string {
  switch (action) {
    case 'asignacion': return 'text-emerald-600 bg-emerald-50';
    case 'liberacion': return 'text-amber-600 bg-amber-50';
    case 'reserva': return 'text-sky-600 bg-sky-50';
    case 'cancelacion_reserva': return 'text-red-500 bg-red-50';
    case 'solicitud_preferida': return 'text-primary-600 bg-primary-100';
    case 'activacion_preferida': return 'text-emerald-600 bg-emerald-50';
    case 'transferencia': return 'text-primary-600 bg-primary-100';
    case 'bloquear': return 'text-red-600 bg-red-50';
    case 'reactivar': return 'text-emerald-600 bg-emerald-50';
    default: return 'text-foreground-500 bg-background-200';
  }
}

function actionIcon(action: FrequencyHistoryEvent['action']): string {
  switch (action) {
    case 'asignacion': return 'ri-link';
    case 'liberacion': return 'ri-link-unlink';
    case 'reserva': return 'ri-bookmark-line';
    case 'cancelacion_reserva': return 'ri-bookmark-2-line';
    case 'solicitud_preferida': return 'ri-mail-send-line';
    case 'activacion_preferida': return 'ri-check-double-line';
    case 'transferencia': return 'ri-arrow-left-right-line';
    case 'bloquear': return 'ri-forbid-line';
    case 'reactivar': return 'ri-play-circle-line';
    default: return 'ri-information-line';
  }
}

export default function FrequencyHistoryModal({ frequency, onClose }: FrequencyHistoryModalProps) {
  const history = useMemo(() => {
    if (!frequency) return [];
    return getFrequencyHistory(frequency.id);
  }, [frequency]);

  if (!frequency) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-foreground-950/40 backdrop-blur-sm" onClick={onClose}></div>
      <div
        className="relative bg-background-50 rounded-2xl w-full max-w-lg max-h-[80vh] shadow-xl overflow-hidden flex flex-col"
        style={{ animation: 'fadeInUp 0.2s ease-out' }}
      >
        {/* Header */}
        <div className="p-5 border-b border-background-200 shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-foreground-950 font-bold text-sm">Historial de Cambios</h3>
              <p className="text-foreground-400 text-xs mt-0.5">
                Frecuencia <span className="font-mono text-foreground-600">{frequency.frequency}</span>
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
            >
              <i className="ri-close-line text-foreground-500 text-sm"></i>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-5 overflow-y-auto flex-1">
          {history.length === 0 ? (
            <div className="text-center py-10">
              <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-background-100 flex items-center justify-center">
                <i className="ri-history-line text-foreground-300 text-xl"></i>
              </div>
              <p className="text-foreground-400 text-sm font-medium">Sin historial aún</p>
              <p className="text-foreground-300 text-xs mt-1">No se registraron cambios para esta frecuencia.</p>
            </div>
          ) : (
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-[19px] top-2 bottom-2 w-px bg-background-200"></div>

              <div className="space-y-4">
                {history.map((event) => (
                  <div key={event.id} className="flex gap-3 relative">
                    {/* Timeline dot */}
                    <div
                      className={`w-[38px] h-[38px] rounded-xl shrink-0 flex items-center justify-center relative z-10 ${
                        actionColor(event.action).split(' ').slice(1).join(' ')
                      }`}
                    >
                      <i className={`${actionIcon(event.action)} ${actionColor(event.action).split(' ')[0]} text-sm`}></i>
                    </div>

                    <div className="min-w-0 flex-1 pt-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${actionColor(event.action)}`}>
                          {actionLabel(event.action)}
                        </span>
                        <span className="text-foreground-400 text-[10px]">{event.timestamp}</span>
                      </div>
                      <p className="text-foreground-700 text-xs mt-1">{event.detail}</p>
                      <p className="text-foreground-300 text-[10px] mt-0.5 flex items-center gap-1">
                        <i className="ri-user-line text-[9px]"></i>
                        {event.performedBy}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-background-200 shrink-0">
          <button
            onClick={onClose}
            className="w-full px-4 py-2.5 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}